import type React from "react";
import { cn } from "../../utils";

const BASE_TYPOGRAPHY_CLASSES = "";

type HeadingProps = {
  isBold?: boolean;
  noWrap?: boolean;
  as?: "span" | "p" | "h1" | "h2" | "h3" | "h4" | "h5" | "h6";
} & React.HTMLAttributes<HTMLHeadingElement>;

type TextProps = {
  isBold?: boolean;
  noWrap?: boolean;
  as?: "span" | "p";
  variant?: "default" | "small" | "large";
} & React.HTMLAttributes<HTMLElement>;

export function TypographyH1({
  as: Component = "h1",
  isBold,
  noWrap,
  className,
  children,
  ...props
}: HeadingProps) {
  return (
    <Component
      {...props}
      className={cn(
        BASE_TYPOGRAPHY_CLASSES,
        "scroll-m-20 text-4xl",
        "tracking-tight text-balance",
        isBold && "font-extrabold",
        noWrap && "text-nowrap",
        className,
      )}
    >
      {children}
    </Component>
  );
}

export function TypographyH2({
  as: Component = "h2",
  isBold,
  noWrap,
  className,
  children,
  ...props
}: HeadingProps) {
  return (
    <Component
      {...props}
      className={cn(
        BASE_TYPOGRAPHY_CLASSES,
        "scroll-m-20 pb-2 text-3xl",
        "tracking-tight first:mt-0",
        isBold && "font-semibold",
        noWrap && "text-nowrap",
        className,
      )}
    >
      {children}
    </Component>
  );
}

export function TypographyH4({
  as: Component = "h4",
  isBold,
  noWrap,
  className,
  children,
  ...props
}: HeadingProps) {
  return (
    <Component
      {...props}
      className={cn(
        BASE_TYPOGRAPHY_CLASSES,
        "scroll-m-20 text-xl tracking-tight",
        isBold && "font-semibold",
        noWrap && "text-nowrap",
        className,
      )}
    >
      {children}
    </Component>
  );
}
export function TypographyCaption({
  as: Component = "span",
  noUppercase,
  children,
  className,
  ...props
}: React.HTMLAttributes<HTMLSpanElement> & {
  as?: "p" | "span";
  noUppercase?: boolean;
}) {
  return (
    <Component
      {...props}
      className={cn(
        BASE_TYPOGRAPHY_CLASSES,
        "text-xs tracking-wide whitespace-nowrap",
        !noUppercase && "uppercase",
        className,
      )}
    >
      {children}
    </Component>
  );
}

export function TypographyText({
  as: Component = "p",
  isBold,
  noWrap,
  className,
  children,
  variant = "default",
  ...rest
}: TextProps) {
  return (
    <Component
      {...rest}
      className={cn(
        BASE_TYPOGRAPHY_CLASSES,
        Component !== "p" && "leading-relaxed",
        Component === "p" && "leading-7 [&:not(:first-child)]:mt-2",
        isBold && "font-semibold",
        noWrap && "text-nowrap",
        variant === "default" && "text-base",
        variant === "small" && "text-sm leading-none font-medium",
        variant === "large" && "text-lg",
        className,
      )}
    >
      {children}
    </Component>
  );
}
